import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Banknote, RefreshCw, Layers } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageTransition } from "@/components/PageTransition";
import { AnimatedMeshGradient } from "@/components/AnimatedMeshGradient";
import { ExpandableCard } from "@/components/ExpandableCard";
import { useT } from "@/lib/i18n";
import { MARKETS } from "@/constants/testIds";

const CAT_ICONS = [BarChart3, Banknote, RefreshCw, Layers];
const REGION_CODES = ["AMR", "EMEA", "APAC"];

export default function Markets() {
  const t = useT();
  const CATEGORIES = t.markets.categories.map((c, i) => ({
    ...c,
    icon: CAT_ICONS[i],
    num: String(i + 1).padStart(2, "0"),
  }));
  const REGIONS = t.markets.regions.map((r, i) => ({ ...r, code: REGION_CODES[i] }));
  return (
    <PageTransition>
      {/* HERO */}
      <section data-testid={MARKETS.hero} className="relative paper-grain overflow-hidden" style={{ background: "var(--nexxis-bg)" }}>
        <AnimatedMeshGradient intensity={1} />
        <div className="hero-fade-bottom" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 pt-36 sm:pt-44 pb-16">
          <Reveal><div className="kicker mb-6">{t.markets.heroKicker}</div></Reveal>
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(40px, 5.5vw, 76px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.markets.heroTitle}
              </h1>
            </Reveal>
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-4">
              <div className="h-px mb-5" style={{ background: "#FF6857", width: 48 }} />
              <p className="font-ui" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 15 }}>
                {t.markets.heroBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-testid={MARKETS.tabs} className="relative" style={{ background: "var(--nexxis-bg-alt)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-[640px] mb-12">
            <Reveal>
              <div className="kicker mb-5">{t.markets.catKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(28px, 3.4vw, 44px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.015em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.markets.catTitle}
              </h2>
            </Reveal>
          </div>
          <div data-testid={MARKETS.table}>
            <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {CATEGORIES.map((c) => (
                <StaggerItem key={c.num}>
                  <ExpandableCard
                    icon={c.icon}
                    num={c.num}
                    title={c.title}
                    body={c.body}
                    bullets={c.bullets}
                  />
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section data-testid={MARKETS.coverage} className="relative paper-grain" style={{ background: "var(--nexxis-bg)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-[640px] mb-12">
            <Reveal>
              <div className="kicker mb-5">{t.markets.regKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(28px, 3.4vw, 44px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.015em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.markets.regTitle}
              </h2>
            </Reveal>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {REGIONS.map((r) => (
              <StaggerItem key={r.code}>
                <div
                  className="nxx-card nxx-card--fill h-full p-7 rounded-[14px]"
                  style={{ background: "var(--nexxis-bg-alt)", border: "1px solid var(--nexxis-border)" }}
                >
                  <div
                    className="font-display"
                    style={{ fontSize: 18, letterSpacing: "0.06em", color: "#0D5F5E" }}
                  >
                    {r.code}
                  </div>
                  <div
                    className="font-display mt-3"
                    style={{ fontSize: 26, color: "var(--nexxis-ink)", fontWeight: 500, letterSpacing: "-0.01em" }}
                  >
                    {r.name}
                  </div>
                  <div className="mt-2 h-px" style={{ background: "#FF6857", width: 32 }} />
                  <p className="font-ui mt-4" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.65, fontSize: 14 }}>
                    {r.body}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <div className="mt-14">
            <Reveal>
              <Link
                to="/partners"
                className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px]"
                style={{ background: "#0D5F5E", color: "#F7F6F2", letterSpacing: "0.04em", fontWeight: 600 }}
              >
                <span>{t.markets.ctaLink}</span>
                <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
